<template>
    <v-container fluid class="pa-6">
        <v-row>
            <v-col>
                <div class="home-greeting">Painel da Devedora</div>
                <div class="home-date">{{ currentDate }}</div>
            </v-col>
        </v-row>

        <v-row v-if="rma" class="mt-4">
            <v-col cols="12">
                <div class="pa-ui">
                    <section class="pa-card rma-home" :class="`rma-home--${rmaTone}`">
                        <div class="rma-home__main">
                            <div class="rma-home__icon">
                                <v-icon size="22">mdi-calendar-check-outline</v-icon>
                            </div>
                            <div class="rma-home__content">
                                <div class="rma-home__title">Fiscalização mensal · {{ rma.label }}</div>
                                <div class="rma-home__text">
                                    {{ rmaDelivered }} · Prazo: {{ rma.due_date_label }}
                                </div>
                                <div class="pa-meter rma-home__meter"><div class="pa-meter__fill" :style="{ width: `${rmaPercent}%` }"></div></div>
                                <div v-if="rmaTone === 'warning'" class="rma-home__notice">
                                    <v-icon small>mdi-clock-alert-outline</v-icon>Prazo encerrado — ainda é possível enviar
                                </div>
                                <div v-else-if="rmaTone === 'success'" class="rma-home__notice">
                                    <v-icon small>mdi-check-circle-outline</v-icon>Todos os documentos do mês foram enviados
                                </div>
                            </div>
                        </div>
                        <v-btn color="primary" depressed to="/fiscalizacao-mensal" class="rma-home__action">
                            {{ rmaTone === "success" ? "Ver documentos" : "Enviar documentos" }}
                        </v-btn>
                    </section>
                </div>
            </v-col>
        </v-row>

        <v-row class="mt-4">
            <v-col v-for="(item, index) in quickAccess" :key="index" cols="12" sm="6" md="3">
                <v-card flat class="quick-access-card" :to="item.route">
                    <v-card-text class="d-flex align-center pa-5">
                        <div class="quick-access-icon mr-4" :style="{ backgroundColor: item.iconBg }">
                            <v-icon size="22" :color="item.iconColor">{{ item.icon }}</v-icon>
                        </div>
                        <div>
                            <div class="quick-access-title">{{ item.name }}</div>
                            <div class="quick-access-desc">{{ item.description }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';
import { eventHub } from '@/main';
import { deliveredText, percent } from '@/components/rma/rma.util';
import '@/components/plan-adhesion/pa-ui.scss';

export default {
    name: 'Home',

    data: () => ({
        // Competencia mais recente da fiscalizacao mensal (null quando o RMA nao esta ativo).
        rma: null,
    }),

    computed: {
        rmaTone() {
            if (!this.rma) return 'neutral';
            if (this.rma.status === 'complete') return 'success';
            return this.rma.overdue ? 'warning' : 'neutral';
        },

        rmaDelivered() {
            return deliveredText(this.rma && this.rma.counts);
        },

        rmaPercent() {
            return percent(this.rma && this.rma.counts);
        },

        currentDate() {
            const date = this.$moment();
            const formatted = date.format('dddd, D [de] MMMM [de] YYYY');
            return formatted.charAt(0).toUpperCase() + formatted.slice(1);
        },

        quickAccess() {
            return [
                {
                    name: 'Credores',
                    description: 'Gerenciar credores',
                    icon: 'mdi-account-group-outline',
                    iconBg: '#EEF2FF',
                    iconColor: '#1E1E2D',
                    route: '/credores',
                },
                {
                    name: 'Hab/Div',
                    description: 'Habilitações e Divergências',
                    icon: 'mdi-sync-alert',
                    iconBg: '#FFF7ED',
                    iconColor: '#F59E0B',
                    route: '/divergencias',
                },
                {
                    name: 'Documentos',
                    description: 'Documentos solicitados (créditos)',
                    icon: 'mdi-file-document-edit-outline',
                    iconBg: '#E8F5E9',
                    iconColor: '#388E3C',
                    route: '/documentos-solicitados',
                },
            ];
        },
    },

    created() {
        this.loadRma();
        eventHub.$on('client-context-changed', this.loadRma);
    },

    beforeDestroy() {
        eventHub.$off('client-context-changed', this.loadRma);
    },

    methods: {
        async loadRma() {
            try {
                const result = await axios.get('/api/client/rma/summary');
                const summary = result.data.content || {};
                this.rma = summary.enabled && summary.current ? summary.current : null;
            } catch (error) {
                this.rma = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.home-greeting {
    font-size: 20px;
    font-weight: 700;
    color: #1E1E2D;
    line-height: 1.3;
}

.home-date {
    font-size: 13px;
    color: #8E8EA1;
    margin-top: 2px;
}

.quick-access-card {
    background-color: #FFFFFF !important;
    cursor: pointer;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
    }
}

.quick-access-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.quick-access-title {
    font-size: 14px;
    font-weight: 600;
    color: #1E1E2D;
    line-height: 1.3;
}

.rma-home {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
    flex-wrap: wrap;
}

.pa-card.rma-home--warning {
    border-color: #fde68a;
    background: #fffdf5;
}

.pa-card.rma-home--success {
    border-color: #bbf7d0;
    background: #f7fdf9;
}

.rma-home__main {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    min-width: 0;
    flex: 1 1 320px;
}

.rma-home__icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #f3f5fb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .v-icon {
        color: #27306b !important;
    }
}

.rma-home--warning .rma-home__icon {
    background: #fef3c7;

    .v-icon {
        color: #b45309 !important;
    }
}

.rma-home--success .rma-home__icon {
    background: #dcfce7;

    .v-icon {
        color: #15803d !important;
    }
}

.rma-home__content {
    min-width: 0;
    flex: 1;
}

.rma-home__title {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.35;
}

.rma-home__text {
    font-size: 13.5px;
    color: #64748b;
    margin-top: 2px;
}

.rma-home__meter {
    margin-top: 8px;
}

.rma-home__notice {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 13px;
    font-weight: 500;

    .v-icon {
        color: inherit !important;
    }
}

.rma-home--warning .rma-home__notice {
    color: #b45309;
}

.rma-home--success .rma-home__notice {
    color: #15803d;
}

.rma-home__action {
    flex-shrink: 0;
}

@media (max-width: 700px) {
    .rma-home__action {
        width: 100%;
    }
}

.quick-access-desc {
    font-size: 12px;
    color: #8E8EA1;
    margin-top: 2px;
}
</style>
